import type { ReactNode } from 'react';
import { useContext } from 'react';
import { Action, Subject } from '@erp/authz';
import { rollupTaskPhase } from '@erp/shared-utils';
import { getExpectedSubtaskTypeKeys } from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { AuthContext } from 'src/context/auth.context';
import { useCan } from 'src/hooks/useCan';
import { useUsersInOrg } from 'src/api/rpc/hooks/core/useUsersInOrg';
import { useListAgentDefinitions } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  useSubtasks,
  useTaskById,
  useTaskTimeline,
} from 'src/api/rpc/hooks/tasks/useTaskDetail';
import { resolveTaskQueryId } from '../taskLookupId';
import type { TaskReviewLoadedProps } from './TaskReviewLoadedProps';

export type TaskReviewLoadState = 'loading' | 'error' | 'missing' | 'ready';

export type TaskReviewModel =
  | { kind: Exclude<TaskReviewLoadState, 'ready'> }
  | { kind: 'ready'; loaded: TaskReviewLoadedProps };

export function useTaskReviewModel({
  taskId,
  fromPage,
  trailing,
}: {
  taskId: string;
  fromPage?: string;
  trailing?: ReactNode;
}): TaskReviewModel {
  const { data: task, isLoading, isError } = useTaskById(taskId);
  const resolvedId = resolveTaskQueryId(taskId, task?.id);
  const { subtasks } = useSubtasks(resolvedId);
  const { data: taskTypes } = useTaskTypes();
  const timeline = useTaskTimeline(resolvedId);
  const { currentUser } = useContext(AuthContext);
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const { data: usersData } = useUsersInOrg();
  const { data: agentData } = useListAgentDefinitions({
    limit: 50,
    enabled: canReadAgents,
  });

  if (isLoading) return { kind: 'loading' };
  if (isError) return { kind: 'error' };
  if (!task) return { kind: 'missing' };

  return {
    kind: 'ready',
    loaded: toLoadedReview({
      task,
      subtasks,
      taskTypes: taskTypes ?? [],
      users: (usersData ?? []).map((row) => ({ id: row.id, name: row.name })),
      agents: (agentData?.definitions ?? []).map((row) => ({
        id: row.id,
        name: row.name,
      })),
      currentUser: currentUser
        ? { id: currentUser.id, name: currentUser.name }
        : null,
      fromPage,
      trailing,
      timeline: {
        view: timeline.view,
        setView: timeline.setView,
        canInspectRuns: canReadAgents,
        error: timeline.error,
        entries: timeline.entries,
        total: timeline.total,
        isLoading: timeline.isLoading,
        hasMore: timeline.hasMore,
        isFetchingMore: timeline.isFetchingMore,
        loadMore: timeline.loadMore,
        refresh: timeline.refresh,
      },
    }),
  };
}

function toLoadedReview({
  task,
  subtasks,
  taskTypes,
  users,
  agents,
  currentUser,
  fromPage,
  trailing,
  timeline,
}: {
  task: TaskResponse;
  subtasks: TaskResponse[];
  taskTypes: TaskTypeResponse[];
  users: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
  currentUser: { id: string; name: string } | null;
  fromPage?: string;
  trailing?: ReactNode;
  timeline: TaskReviewLoadedProps['timeline'];
}): TaskReviewLoadedProps {
  const typeById = new Map(taskTypes.map((t) => [t.id, t]));
  const typeNameById = new Map(taskTypes.map((t) => [t.id, t.name]));
  const typeNameByKey = new Map(taskTypes.map((t) => [t.key, t.name]));
  const userNameById = new Map(users.map((u) => [u.id, u.name]));
  if (currentUser) userNameById.set(currentUser.id, currentUser.name);
  const taskType = task.taskTypeId ? typeById.get(task.taskTypeId) : undefined;

  return {
    task,
    taskType,
    typeName: task.taskTypeId ? typeNameById.get(task.taskTypeId) : undefined,
    typeNameById,
    typeNameByKey,
    subtasks,
    rollup: rollupTaskPhase(
      getExpectedSubtaskTypeKeys(taskType?.expectedSubtaskTypeKeys),
      subtasks.map((s) => ({
        typeKey: s.taskTypeId
          ? (typeById.get(s.taskTypeId)?.key ?? null)
          : null,
        status: s.status,
      })),
    ),
    users,
    agents,
    userNameById,
    agentNameById: new Map(agents.map((a) => [a.id, a.name])),
    currentUserId: currentUser?.id,
    fromPage,
    trailing,
    timeline,
  };
}
